import { useEffect, useState } from "react";
import { Commentary } from "../Commentary";
import { AnimatePresence, motion } from "framer-motion";
import { CommentaryEvents, EulerStateEvents, GameStateEvents } from "../Consts";
import { StateManager } from "../GameStateManager";
import { EulerStateManager } from "../EulerStateManager";
import { useTranslation } from "../../i18n/useTranslation";

const defaultCommentary = "Welcome to Virtually Sports Horse Racing!";

interface CommentaryBarProps {
  showCommentary: boolean;
}

export function CommentaryBar({ showCommentary }: CommentaryBarProps) {
  const { t } = useTranslation();
  const [commentary, setCommentary] = useState(defaultCommentary);

  function handleCommentary() {
    const comm = Commentary.getInstance();
    const latestComm = comm.getLatestCommentary();
    setCommentary(latestComm);
  }

  function resetCommentary() {
    setCommentary(defaultCommentary);
  }

  function aboutToBegin() {
    setCommentary(t("commentary-bar.pre-start"));
  }

  function startRace() {
    setCommentary(t("commentary-bar.race-start"));
  }

  useEffect(() => {
    const gameSate = StateManager.getInstance();
    const comm = Commentary.getInstance();
    const eulerState = EulerStateManager.getInstance();
    eulerState.subscribeToEvent(
      EulerStateEvents.ON_ENTER_GET_NEXT_EVENT_STATE,
      resetCommentary,
    );

    eulerState.subscribeToEvent(
      EulerStateEvents.ON_ENTER_NO_MORE_BETS_STATE,
      aboutToBegin,
    );

    gameSate.subscribeToEvent(
      GameStateEvents.ON_ENTER_RACE_IN_PROGRESS_STATE,
      startRace,
    );

    comm.subscribeToEvent(
      CommentaryEvents.ON_COMMENTARY_UPDATED,
      handleCommentary,
    );

    return () => {
      gameSate.unsubscribeFromEvent(
        GameStateEvents.ON_ENTER_RACE_IN_PROGRESS_STATE,
        startRace,
      );
      eulerState.unsubscribeFromEvent(
        EulerStateEvents.ON_ENTER_NO_MORE_BETS_STATE,
        aboutToBegin,
      );

      eulerState.unsubscribeFromEvent(
        EulerStateEvents.ON_ENTER_GET_NEXT_EVENT_STATE,
        resetCommentary,
      );

      comm.unsubscribeFromEvent(
        CommentaryEvents.ON_COMMENTARY_UPDATED,
        handleCommentary,
      );
    };
  }, []);

  return (
    <AnimatePresence>
      {showCommentary && (
        <motion.div
          animate={{ x: 0, opacity: 1 }}
          initial={{ x: "-100%", opacity: 0 }}
          exit={{ x: "-100%", opacity: 0 }}
          transition={{ duration: 0.2, ease: "linear" }}
          className="flex items-center justify-center w-full h-full p-1 md:p-4 bg-slate-900/70 backdrop-blur-md"
        >
          <p className="max-w-4xl text-sm italic font-semibold text-center text-white md:text-lg lg:text-xl">
            {commentary}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
